import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { signatureStyles, createSignatureSession, generateSignatures } from '../services/signatureService'

const { FiCheck, FiLoader, FiDownload, FiLock } = FiIcons

const SignatureGenerator = () => {
  const [formData, setFormData] = useState({
    name: '',
    profession: '',
    style: 'elegant'
  })
  const [currentStep, setCurrentStep] = useState('form') // form, generating, preview, payment, processing, completed
  const [sessionId, setSessionId] = useState(null)
  const [previewUrls, setPreviewUrls] = useState([])
  const [selectedUrl, setSelectedUrl] = useState('')
  const [finalUrls, setFinalUrls] = useState({})
  const [progress, setProgress] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const [isRedirecting, setIsRedirecting] = useState(false)

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleStyleSelect = (style) => {
    setFormData({
      ...formData,
      style
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    try {
      setCurrentStep('generating')
      setProgress(10)
      setProgressMessage('Creating your signature session...')

      // Create session
      const session = await createSignatureSession(formData.name, formData.style)
      setSessionId(session.id)
      setProgress(30)
      setProgressMessage('Generating signature variations...')

      // Generate signatures
      const urls = await generateSignatures(session.id, formData.name, formData.style)
      setPreviewUrls(urls)
      setProgress(100)
      setProgressMessage('Ready!')

      setTimeout(() => {
        setCurrentStep('preview')
      }, 1000)

    } catch (error) {
      console.error('Error generating signatures:', error)
      const hint =
        import.meta.env.DEV && error?.message
          ? `\n\n(${error.message})`
          : ''
      alert(`Error generating signatures. Please try again.${hint}`)
      setCurrentStep('form')
    }
  }

  const handleSelectSignature = async (url) => {
    if (!sessionId || !url) {
      alert('Missing session information. Please try again.')
      return
    }

    setIsRedirecting(true)

    try {
      // Create Stripe Checkout Session
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionId,
          selectedUrl: url,
          name: formData.name
        })
      })

      if (!response.ok) {
        // Try to parse JSON error, but handle HTML error pages too
        let errorMessage = 'Failed to create checkout session'
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch (parseError) {
          // If response is not JSON (e.g., HTML error page), get text
          const errorText = await response.text()
          console.error('Non-JSON error response:', errorText)
          errorMessage = `Server error (${response.status}): ${errorText.substring(0, 100)}`
        }
        throw new Error(errorMessage)
      }

      const { url: checkoutUrl } = await response.json()

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl
    } catch (error) {
      console.error('Checkout error:', error)
      alert(`Payment error: ${error.message}. Please try again.`)
      setIsRedirecting(false)
    }
  }


  const downloadFile = async (url, filename) => {
    try {
      console.log('Downloading file:', { url, filename })
      
      // Check if URL is valid
      if (!url || url === '') {
        console.error('Invalid URL for download:', url)
        alert('Error: Invalid file URL. Please try regenerating.')
        return
      }

      // For cross-origin URLs, we need to fetch and create a blob
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl)
      
      console.log('Download completed:', filename)
    } catch (error) {
      console.error('Error downloading file:', error)
      alert(`Error downloading ${filename}. Please try again.`)
    }
  }

  return (
    <section id="generate" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
            Generate Your Signature
          </h2>
          <p className="text-xl text-gray-600">
            Create a professional signature in seconds
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {currentStep === 'form' && (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Style Selection */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-4">
                    Choose Your Style
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(signatureStyles).map(([key, style]) => (
                      <motion.button
                        key={key}
                        type="button"
                        onClick={() => handleStyleSelect(key)}
                        className={`p-6 rounded-lg border-2 text-left transition-all ${
                          formData.style === key
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{style.name}</h3>
                          {formData.style === key && (
                            <SafeIcon icon={FiCheck} className="text-primary-600" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{style.description}</p>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Name Input */}
                <div>
                  <label htmlFor="name" className="block text-lg font-semibold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Profession Input - Hidden for now */}
                {/* <div>
                  <label htmlFor="profession" className="block text-lg font-semibold text-gray-900 mb-2">
                    Profession (Optional)
                  </label>
                  <input
                    type="text"
                    id="profession"
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                    placeholder="e.g., CEO, Designer, Consultant"
                  />
                </div> */}

                <motion.button
                  type="submit"
                  className="w-full bg-primary-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!formData.name.trim()}
                >
                  Generate My Signature
                </motion.button>
              </form>
            </motion.div>
          )}

          {(currentStep === 'generating' || currentStep === 'processing') && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-12"
            >
              <div className="mb-8">
                <SafeIcon icon={FiLoader} className="text-6xl text-primary-600 animate-spin mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">{progressMessage}</h3>
                <div className="w-full bg-gray-200 rounded-full h-3 max-w-md mx-auto">
                  <motion.div
                    className="bg-primary-600 h-3 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <p className="text-gray-600 mt-2">{progress}% complete</p>
              </div>
            </motion.div>
          )}

          {currentStep === 'preview' && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Choose Your Favorite</h3>
                <p className="text-gray-600">Select the signature that best represents you</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {previewUrls.map((url, index) => (
                  <motion.div
                    key={index}
                    className="p-6 border-2 border-gray-200 rounded-lg hover:border-primary-600 transition-all"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="relative">
                      <img 
                        src={url} 
                        alt={`Signature option ${index + 1}`}
                        className="w-full h-32 object-contain mb-4 select-none pointer-events-none"
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                      />
                      {/* Watermark overlay */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black bg-opacity-20 text-white px-2 py-1 rounded text-xs font-semibold transform rotate-12">
                          PREVIEW
                        </div>
                      </div>
                    </div>
                    <motion.button
                      onClick={() => handleSelectSignature(url)}
                      disabled={isRedirecting}
                      className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      whileTap={{ scale: 0.98 }}
                    >
                      {isRedirecting ? (
                        <>
                          <SafeIcon icon={FiLoader} className="animate-spin text-sm" />
                          <span>Redirecting...</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiLock} className="text-sm" />
                          <span>Purchase €14.95</span>
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                ))}
              </div>

              <div className="text-center mt-8">
                <div className="bg-green-100 border border-green-300 rounded-lg p-4 inline-block">
                  <p className="text-green-800 font-semibold">🎉 50% OFF Limited Time!</p>
                  <p className="text-green-700 text-sm">Regular price €29.95 • Now only €14.95</p>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 'completed' && (
            <motion.div
              key="completed"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center mb-8">
                <SafeIcon icon={FiCheck} className="text-6xl text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Your Signature is Ready!</h3>
                <p className="text-gray-600">Download in your preferred format</p>
                {/* Debug info - remove in production */}
                <div className="text-xs text-gray-400 mt-2">
                  <p>JPG: {finalUrls.jpg ? '✅' : '❌'}</p>
                  <p>PNG: {finalUrls.png ? '✅' : '❌'}</p>
                  <p>SVG: {finalUrls.svg ? '✅' : '❌'}</p>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg mb-8">
                <img 
                  src={selectedUrl} 
                  alt="Your selected signature"
                  className="w-full max-w-md mx-auto h-32 object-contain"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <motion.button
                  onClick={() => downloadFile(finalUrls.jpg, `${formData.name}-signature.jpg`)}
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Download JPG</span>
                </motion.button>

                <motion.button
                  onClick={() => downloadFile(finalUrls.png, `${formData.name}-signature.png`)}
                  className="flex items-center justify-center space-x-2 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Download PNG</span>
                </motion.button>

                <motion.button
                  onClick={() => downloadFile(finalUrls.svg, `${formData.name}-signature.svg`)}
                  className="flex items-center justify-center space-x-2 bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Download SVG</span>
                </motion.button>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => {
                    setCurrentStep('form')
                    setFormData({ name: '', profession: '', style: 'elegant' })
                    setPreviewUrls([])
                    setSelectedUrl('')
                    setFinalUrls({})
                    setProgress(0)
                    setIsRedirecting(false)
                  }}
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Create Another Signature
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}

export default SignatureGenerator